import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import { Person, Row, Scorecard } from 'talent_matching/types';
import LocationCell from 'talent_matching/components/table/location_cell';
import MatchStrengthCell from 'talent_matching/components/table/match_strength_cell';
import ResumeDetailsCell from 'talent_matching/components/table/resume_details_cell';
import CandidateOverviewCell from 'talent_matching/components/table/candidate_overview_cell';
import CandidateRowActions from 'talent_matching/components/table/candidate_row_actions';
import FeedbackCell from 'talent_matching/components/table/feedback_cell';
import { CellParamsDef } from '@grnhse/seedling/lib/birch/components/table';
import TagsCell from 'talent_matching/components/shared/tags_cell';
import { HelpTooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import { format } from 'date-fns';
import { withLocalePrefix } from 'shared/utils/translation';
import { useResizeObserver } from 'shared/utils/useResizeObserver';
import { SelectionsHelper } from 'talent_matching/hooks/use_selections_helper';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { useTalentMatchingTabContext } from 'talent_matching/contexts/talent_matching_tab_context';
import {
  getAnswerForQuestion,
  type QuestionColumn,
} from 'talent_matching/components/table/question_columns';
import styled from 'styled-components';
import { Tooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import { ACTIONS_COLUMN_FIELD } from 'talent_matching/hooks/use_column_visibility';

const t = withLocalePrefix('talent_matching');

type ColumnsOptions = {
  selectionsHelper: SelectionsHelper;
  isPanelOpen: boolean;
  selectedRow: Row | null;
  openCalibrationModal: () => void;
  onRejectSuccess: (row: Row, closeModal: () => void) => void;
  questionColumns: QuestionColumn[];
};

const QuestionAnswerText = styled.div`
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 8;
  -webkit-box-orient: vertical;
  word-break: break-word;
  color: inherit;
  font: inherit;
`;

const QuestionAnswerTooltipContent = styled.div`
  max-width: 400px;
  word-break: break-word;
`;

type QuestionAnswerCellProps = {
  html: string;
};

const QuestionAnswerCell = ({ html }: QuestionAnswerCellProps) => {
  const textRef = useRef<HTMLDivElement | null>(null);
  const [isTruncated, setIsTruncated] = useState<boolean>(false);

  const updateIsTruncated = useCallback(() => {
    const el = textRef.current;
    if (!el) {
      return;
    }

    setIsTruncated(el.scrollHeight > el.clientHeight + 1);
  }, []);

  useEffect(() => {
    updateIsTruncated();
  }, [html, updateIsTruncated]);

  useResizeObserver(updateIsTruncated, textRef);

  const cellContent = (
    <SecondaryBody as="div">
      <QuestionAnswerText
        ref={textRef}
        className="pii-blur"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </SecondaryBody>
  );

  if (!isTruncated) {
    return cellContent;
  }

  return (
    <Tooltip
      ariaLabel="Full answer"
      label={
        <QuestionAnswerTooltipContent
          className="pii-blur"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      }
    >
      {cellContent}
    </Tooltip>
  );
};

export const useColumns = ({
  selectionsHelper,
  isPanelOpen,
  selectedRow,
  openCalibrationModal,
  onRejectSuccess,
  questionColumns,
}: ColumnsOptions) => {
  const { canUseLocations, userDateFormat } = useUserContext();
  const { canParseResumes, canViewTalentMatchingAiMatchScore } =
    useTalentMatchingTabContext();

  let dateFormat: string;
  switch (userDateFormat) {
    case 'day_month_year':
      dateFormat = 'd LLLL yyyy';
      break;
    case 'year_month_day':
      dateFormat = 'yyyy LLLL d';
      break;
    default:
      dateFormat = 'LLLL d, yyyy';
  }

  const formatDate = (date: string) => {
    return format(new Date(date), dateFormat);
  };

  return [
    {
      field: 'fullName',
      headerName: t('table.columns.candidate'),
      cell: (params: CellParamsDef) => {
        return (
          <CandidateOverviewCell
            candidate={params.row as Person}
            isRowSelected={selectionsHelper.isRowSelected(params.id)}
            isInSidepanel={isPanelOpen && params.id === selectedRow?.id}
            onRejectSuccess={onRejectSuccess}
          />
        );
      },
    },
    ...(canParseResumes
      ? [
          {
            field: 'resumeDetails',
            headerName: t('table.columns.resume_details'),
            cell: (params: CellParamsDef) => {
              return <ResumeDetailsCell candidate={params.row as Person} />;
            },
          },
        ]
      : []),
    ...(canViewTalentMatchingAiMatchScore
      ? [
          {
            field: 'match',
            headerName: t('table.columns.match_strength.title'),
            minWidth: 126,
            renderHeader: () => (
              <Stack direction="row" spacing={1}>
                <SecondaryBody medium>
                  {t('table.columns.match_strength.title')}
                </SecondaryBody>
                <HelpTooltip
                  ariaLabel={t('table.columns.match_strength.description')}
                  label={t('table.columns.match_strength.description')}
                ></HelpTooltip>
              </Stack>
            ),
            description: t('table.columns.match_strength.description'),
            cell: (params: CellParamsDef) => {
              return (
                <MatchStrengthCell
                  candidate={params.row as Person}
                  openCalibrationModal={openCalibrationModal}
                />
              );
            },
          },
        ]
      : []),
    {
      field: 'applicationInfo',
      headerName: t('table.columns.application_info'),
      cell: (params: CellParamsDef) => {
        return (
          <Stack
            spacing={0.5}
            sx={{
              padding: 1,
              paddingLeft: 0,
              height: '100%',
            }}
          >
            <SecondaryBody>
              {t('table.labels.applied_on', {
                date: formatDate(params.row.appliedOn),
              })}
            </SecondaryBody>
            {params.row.sourceName && (
              <SecondaryBody metadata>{params.row.sourceName}</SecondaryBody>
            )}
          </Stack>
        );
      },
    },
    ...questionColumns.map(({ field, label, description, question }) => ({
      field: field,
      headerName: label,
      ...(description
        ? {
            renderHeader: () => (
              <Stack direction="row" spacing={1}>
                <SecondaryBody medium>{label}</SecondaryBody>
                <HelpTooltip ariaLabel={description} label={description} />
              </Stack>
            ),
          }
        : {}),
      cell: (params: CellParamsDef) => {
        const answer = getAnswerForQuestion(params.row as Person, question);
        return (
          <Stack
            spacing={1}
            sx={{
              padding: 1,
              paddingLeft: 0,
              height: '100%',
            }}
          >
            <QuestionAnswerCell html={answer || '--'} />
          </Stack>
        );
      },
    })),
    ...(canUseLocations
      ? [
          {
            field: 'location',
            headerName: t('table.columns.location'),
            minWidth: 110,
            renderHeader: () => (
              <Stack direction="row" spacing={1}>
                <SecondaryBody medium>
                  {t('table.columns.location')}
                </SecondaryBody>
                <HelpTooltip
                  ariaLabel={t('table.columns.location_tooltip')}
                  label={t('table.columns.location_tooltip')}
                ></HelpTooltip>
              </Stack>
            ),
            cell: (params: CellParamsDef) => {
              const loc = (params.row as Person).profileLocation;
              return <LocationCell location={loc} />;
            },
          },
        ]
      : []),
    {
      field: 'currentFeedback',
      headerName: t('table.columns.feedback'),
      cell: (params: CellParamsDef) => {
        return (
          <FeedbackCell scorecards={params.row.scorecards as Scorecard[]} />
        );
      },
    },
    {
      field: 'schools',
      headerName: t('table.columns.schools'),
      cell: (params: CellParamsDef) => {
        return (
          <Stack
            spacing={1}
            sx={{
              padding: 1,
              paddingLeft: 0,
              height: '100%',
            }}
          >
            {params.row.educations?.map(
              (edu: { school_name_id: { label: string; value: string } }) => {
                if (!edu.school_name_id) {
                  return null;
                }
                return (
                  <SecondaryBody key={edu.school_name_id.value}>
                    {edu.school_name_id.label}
                  </SecondaryBody>
                );
              }
            )}
          </Stack>
        );
      },
    },
    {
      field: 'degree',
      headerName: t('table.columns.degree'),
      cell: (params: CellParamsDef) => {
        return (
          <Stack
            spacing={1}
            sx={{
              padding: 1,
              paddingLeft: 0,
              height: '100%',
            }}
          >
            {params.row.educations?.map(
              (edu: { degree_id: { label: string; value: string } }) => {
                if (!edu.degree_id) {
                  return null;
                }
                return (
                  <SecondaryBody key={edu.degree_id.value}>
                    {edu.degree_id.label}
                  </SecondaryBody>
                );
              }
            )}
          </Stack>
        );
      },
    },
    {
      field: 'discipline',
      headerName: t('table.columns.discipline'),
      cell: (params: CellParamsDef) => {
        return (
          <Stack
            spacing={1}
            sx={{
              padding: 1,
              paddingLeft: 0,
              height: '100%',
            }}
          >
            {params.row.educations?.map(
              (edu: { discipline_id: { label: string; value: string } }) => {
                if (!edu.discipline_id) {
                  return null;
                }
                return (
                  <SecondaryBody key={edu.discipline_id.value}>
                    {edu.discipline_id.label}
                  </SecondaryBody>
                );
              }
            )}
          </Stack>
        );
      },
    },
    {
      field: 'tags',
      headerName: t('table.columns.tags'),
      cell: (params: CellParamsDef) => {
        return <TagsCell tags={(params.row as Person).tags} />;
      },
    },
    {
      field: 'jobPost',
      headerName: t('table.columns.job_post'),
      cell: (params: CellParamsDef) => {
        return (
          <Stack
            sx={{
              padding: 1,
              paddingLeft: 0,
              height: '100%',
            }}
          >
            <SecondaryBody>{params.row.jobPost?.title}</SecondaryBody>
          </Stack>
        );
      },
    },
    {
      field: ACTIONS_COLUMN_FIELD,
      headerName: '',
      width: 32,
      resizable: false,
      cell: (params: CellParamsDef) => {
        return <CandidateRowActions candidate={params.row as Person} />;
      },
    },
  ];
};
